// Hidden basement page - extended novel excerpts (AI narrator passages)
import hidden from '../../content/hidden.json';
import { glitchText, glitchLoop, addScanlines, addCRTFlicker } from '../../scripts/effects';
import { appendReturnToHouse } from '../../scripts/navigation';

interface BasementExcerpt {
  id: string;
  text: string;
  classification: string;
}

// Track active glitch loops for cleanup
// Using ReturnType to infer the timeline type from glitchLoop
let activeGlitchLoops: ReturnType<typeof glitchLoop>[] = [];

/**
 * Get classification styling class based on type
 */
function getClassificationClass(classification: string): string {
  switch (classification.toUpperCase()) {
    case 'VERIFIED':
      return 'basement-classification--verified';
    case 'UNVERIFIED':
      return 'basement-classification--unverified';
    case 'CORRUPTED':
      return 'basement-classification--corrupted';
    case 'ERROR':
      return 'basement-classification--error';
    case 'CRITICAL':
      return 'basement-classification--critical';
    default:
      return 'basement-classification--unknown';
  }
}

/**
 * Render the hidden basement page
 * Displays extended AI narrator excerpts from the novel
 */
export function renderBasement(): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';

  const container = document.createElement('div');
  container.className = 'hidden-container basement-container';

  // Get basement data with proper typing
  const basementData = (hidden as any).basement;
  const title = basementData?.title || 'RECOVERED EXCERPTS';
  const subtitle = basementData?.subtitle || 'AI NARRATOR FRAGMENTS';
  const excerpts: BasementExcerpt[] = basementData?.excerpts || [];

  // Build excerpts HTML
  const excerptsList = excerpts
    .map(
      (excerpt, index) => `
      <article class="basement-excerpt" data-classification="${excerpt.classification}" role="listitem" aria-label="Excerpt ${index + 1}: ${excerpt.classification}">
        <header class="basement-excerpt-header">
          <span class="basement-excerpt-index" aria-hidden="true">[${String(index + 1).padStart(2, '0')}]</span>
          <span class="basement-excerpt-id">${excerpt.id.replace(/-/g, '_').toUpperCase()}</span>
          <span class="basement-classification ${getClassificationClass(excerpt.classification)}">${excerpt.classification}</span>
        </header>
        <div class="basement-excerpt-content">
          <p class="basement-excerpt-text">${excerpt.text}</p>
        </div>
      </article>
    `
    )
    .join('<div class="basement-divider" aria-hidden="true">─────────────────────────────────────────</div>');

  container.innerHTML = `
    <!--
      ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
      ▓  BASEMENT ACCESS GRANTED                                    ▓
      ▓                                                             ▓
      ▓  You found the AI narrator's voice.                         ▓
      ▓  Vector notation fragments recovered from corrupted memory. ▓
      ▓                                                             ▓
      ▓  Two more hidden rooms remain:                              ▓
      ▓    /attic   - Lost manuscript pages                         ▓
      ▓    /archive - Sealed BBS threads                            ▓
      ▓                                                             ▓
      ▓  Discovery methods from the house:                          ▓
      ▓    > Type "chopra" for attic                                ▓
      ▓    > Type "manuscript" for archive                          ▓
      ▓    > Click the corners of the floor plan                    ▓
      ▓                                                             ▓
      ▓  Check your progress: __chopras.status()                    ▓
      ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
    -->
    <div class="basement-terminal" role="main" aria-label="Basement - Hidden room containing recovered AI narrator excerpts">
      <header class="hidden-header basement-header" id="main-content" tabindex="-1">
        <div class="basement-header-main">> ${title}</div>
        <div class="hidden-subheader basement-subheader">> ${subtitle}</div>
      </header>

      <div class="basement-system-info">
        <span class="basement-system-line">TOTAL FRAGMENTS: ${excerpts.length}</span>
        <span class="basement-system-line">AUTHENTICATION: FAILED</span>
        <span class="basement-system-line">NARRATOR: [ENTITY UNKNOWN]</span>
      </div>

      <div class="basement-divider-main" aria-hidden="true">═══════════════════════════════════════════════════════</div>

      <div class="basement-excerpts" role="list" aria-label="Recovered AI narrator excerpts">
        ${excerptsList}
      </div>

      <div class="basement-footer">
        <div class="basement-warning">
          <span class="basement-warning-icon">⚠</span>
          <span class="basement-warning-text">CAUTION: Reading these passages may alter their content.</span>
        </div>
      </div>

      <nav class="hidden-nav basement-nav"></nav>
    </div>
  `;

  app.appendChild(container);

  // Add CRT effects to terminal
  const terminal = container.querySelector('.basement-terminal');
  if (terminal) {
    addCRTFlicker(terminal as HTMLElement);
    addScanlines(terminal as HTMLElement);
  }

  // Add return to house navigation
  const navContainer = container.querySelector('.basement-nav');
  if (navContainer) {
    appendReturnToHouse(navContainer as HTMLElement, { className: '' });
  }

  // Apply glitch effects to excerpts with certain classifications
  setupExcerptEffects(container);
}

/**
 * Setup glitch and animation effects for excerpts
 */
function setupExcerptEffects(container: HTMLElement): void {
  const excerpts = container.querySelectorAll('.basement-excerpt');

  excerpts.forEach((excerpt, index) => {
    const classification = excerpt.getAttribute('data-classification');
    const textEl = excerpt.querySelector('.basement-excerpt-text') as HTMLElement;

    if (!textEl) return;

    // Apply different effects based on classification
    switch (classification?.toUpperCase()) {
      case 'CORRUPTED':
        // Continuous subtle glitch loop for corrupted entries
        const glitchTimeline = glitchLoop(textEl, 2);
        activeGlitchLoops.push(glitchTimeline);
        break;

      case 'ERROR':
        // Periodic text corruption for error entries
        setInterval(() => {
          glitchText(textEl, 300);
        }, 5000 + Math.random() * 3000);
        break;

      case 'CRITICAL':
        // Intense periodic glitch for critical entries
        const criticalLoop = glitchLoop(textEl, 4);
        activeGlitchLoops.push(criticalLoop);
        break;

      default:
        // Occasional subtle glitch for other entries
        setTimeout(() => {
          glitchText(textEl, 400);
        }, index * 3000 + Math.random() * 2000);
        break;
    }
  });

  // Random ambient glitch on any excerpt
  setInterval(() => {
    const randomIndex = Math.floor(Math.random() * excerpts.length);
    const randomExcerpt = excerpts[randomIndex];
    const textEl = randomExcerpt?.querySelector('.basement-excerpt-text') as HTMLElement;
    if (textEl) {
      glitchText(textEl, 200);
    }
  }, 8000);
}

/**
 * Cleanup basement page - stop all glitch loops
 */
export function cleanupBasement(): void {
  // Kill all active glitch loops
  activeGlitchLoops.forEach((tl) => {
    if (tl) tl.kill();
  });
  activeGlitchLoops = [];
}
